import { useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import {
  showGlobalLoading,
  hideGlobalLoading,
} from "../../components/GlobalLoadingIndicator";
import DateRangePicker from "../../components/DateRangePicker";
import { getWorkerTeamContext } from "../../services/teams";
import { getWorkerIdForUser } from "../../services/workers";
import { useAuth } from "../../contexts/AuthContext";
import { useAdminAccess } from "../../hooks/useAdminAccess";
import { useRefresh } from "../../contexts/RefreshContext";
import type { TeamSelection } from "./TeamSelector";
import { isValidDate } from "../../utils/dateInput";

interface LeaderboardEntry {
  worker_id: number;
  name: string;
  contract_count: number;
  revenue?: number;
}

interface LeaderboardRpcEntry {
  worker_id: number;
  name: string;
  contract_count: number;
  revenue: number | null;
}

type RankingMode = "contracts" | "revenue";

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

interface SalesLeaderboardProps {
  selectedOption: "today" | "yesterday" | "custom";
  dateRange: DateRange;
  isAdminRole: boolean;
  teamSelection: TeamSelection;
  setSelectedOption: (option: "today" | "yesterday" | "custom") => void;
  setDateRange: (range: DateRange) => void;
}

const SalesLeaderboard: React.FC<SalesLeaderboardProps> = function ({
  selectedOption,
  dateRange,
  isAdminRole,
  teamSelection,
  setSelectedOption,
  setDateRange,
}) {
  const { user } = useAuth();
  const { hasAccess, roleId, loading: accessLoading } = useAdminAccess();
  const canViewRevenue =
    !accessLoading && (roleId === 1 || roleId === 2 || roleId === 3);
  const { addRefreshListener } = useRefresh();
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>(
    []
  );
  const [error, setError] = useState<string | null>(null);
  const [selectedRankingMode, setRankingMode] =
    useState<RankingMode>("contracts");
  const rankingMode: RankingMode = canViewRevenue
    ? selectedRankingMode
    : "contracts";
  const [leaderboardCache, setLeaderboardCache] = useState<
    Record<string, LeaderboardEntry[]>
  >({});
  const [currentWorkerId, setCurrentWorkerId] = useState<number | null>(null);
  const [teamContext, setTeamContext] = useState<{
    teamCount: number;
    teams: any[];
    hasMultipleTeams: boolean;
    hasNoTeams: boolean;
  } | null>(null);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  // Get leaderboard title based on context
  const getLeaderboardTitle = (): string => {
    if (hasAccess || roleId === 3) {
      // Admin users or Team Leads
      if (teamSelection.type === "team") return "Team-Rangliste";
      if (teamSelection.type === "all_my_teams") return "Meine Teams Rangliste";
      return "Verkaufsrangliste";
    } else {
      // Normal users don't see leaderboards
      return "Verkaufsrangliste";
    }
  };

  // Get leaderboard subtitle based on context
  const getLeaderboardSubtitle = (): string => {
    const timeText =
      selectedOption === "today"
        ? "heute"
        : selectedOption === "yesterday"
        ? "gestern"
        : "im ausgewählten Zeitraum";

    if (hasAccess || (roleId === 3 && teamSelection.type === "team")) {
      // Admin users or Team Leads viewing specific team
      const prefix =
        teamSelection.type === "team"
          ? "Beste Team-Verkäufer"
          : "Beste Verkäufer";
      return `${prefix} ${timeText}`;
    } else {
      // Normal users - show team names they are part of
      if (teamContext && teamContext.teams.length > 0) {
        const teamNames = teamContext.teams.map((team) => team.name).join(", ");
        return `Rangliste für: ${teamNames}`;
      }
      return "Rangliste für deine Teams";
    }
  };

  // Helper to create a cache key
  const getCacheKey = (
    mode: RankingMode,
    option: string,
    range: DateRange,
    teamSel: TeamSelection,
    workerId?: number
  ) => {
    const start = isValidDate(range.startDate)
      ? range.startDate.toISOString()
      : "";
    const end = isValidDate(range.endDate) ? range.endDate.toISOString() : "";
    const teamKey =
      teamSel.type === "team" ? `team-${teamSel.teamId}` : teamSel.type;
    const workerKey = workerId ? `worker-${workerId}` : "";
    return `${mode}|${option}|${start}|${end}|${teamKey}|${workerKey}`;
  };

  // Initialize current worker and team context
  useEffect(() => {
    const initializeWorkerContext = async () => {
      if (!user?.id) return;

      try {
        const workerId = await getWorkerIdForUser(user.id);
        setCurrentWorkerId(workerId);

        if (!hasAccess) {
          // For normal users, get their team context
          const context = await getWorkerTeamContext(workerId);
          setTeamContext(context);
        }
      } catch (error) {
        console.error("Error initializing worker context:", error);
      }
    };

    initializeWorkerContext();
  }, [user?.id, hasAccess]);

  const fetchLeaderboardData = async (
    showLoading = true,
    forceRefresh = false
  ) => {
    if (accessLoading || roleId === null || currentWorkerId === null) {
      return;
    }

    const cacheKey = getCacheKey(
      rankingMode,
      selectedOption,
      dateRange,
      teamSelection,
      currentWorkerId || undefined
    );

    if (!forceRefresh && leaderboardCache[cacheKey]) {
      setLeaderboardData(leaderboardCache[cacheKey] ?? []);
      return;
    }

    if (showLoading) showGlobalLoading();
    try {
      setError(null);
      let startDateStr: string | null = null;
      let endDateStr: string | null = null;
      let localStartDate: Date | null = null;
      let localEndDate: Date | null = null;

      if (selectedOption === "today" || selectedOption === "yesterday") {
        const dayOffset = selectedOption === "today" ? 0 : -1;
        const { data: rangeData, error: rangeError } = await supabase.rpc(
          "get_utc_range_for_local_day",
          { tz_name: "Europe/Berlin", day_offset: dayOffset }
        );

        if (rangeError) {
          console.error("Error fetching date range from RPC:", rangeError);
          setError(rangeError.message);
          hideGlobalLoading();
          return;
        }

        if (
          Array.isArray(rangeData) &&
          rangeData.length > 0 &&
          rangeData[0] &&
          rangeData[0].start_utc &&
          rangeData[0].end_utc
        ) {
          startDateStr = rangeData[0].start_utc;
          endDateStr = rangeData[0].end_utc;

          localStartDate = new Date(startDateStr!);
          localEndDate = new Date(new Date(endDateStr!).getTime() - 1);
        } else {
          console.error(
            "RPC did not return expected date range object or fields:",
            rangeData
          );
          setError("Failed to calculate date range via RPC.");
          hideGlobalLoading();
          return;
        }
      } else {
        // Custom date range
        localStartDate = isValidDate(dateRange.startDate)
          ? new Date(dateRange.startDate)
          : new Date();
        localStartDate.setHours(0, 0, 0, 0);
        localEndDate = isValidDate(dateRange.endDate)
          ? new Date(dateRange.endDate)
          : new Date();
        localEndDate.setHours(23, 59, 59, 999);

        startDateStr = localStartDate.toISOString();
        endDateStr = localEndDate.toISOString();
      }

      if (!startDateStr || !endDateStr || !localStartDate || !localEndDate) {
        setError("Failed to determine date range.");
        hideGlobalLoading();
        return;
      }

      const formatDateToGermanDateString = (date: Date): string => {
        const formatter = new Intl.DateTimeFormat("en-CA", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          timeZone: "Europe/Berlin",
        });
        return formatter.format(date);
      };

      // Roles 4+ always receive their active teams' aggregate leaderboard.
      // The database ignores client selection for these roles and validates
      // role 1-3 selections before reading any underlying rows.
      const effectiveSelection =
        roleId !== null && roleId >= 4 ? "all_my_teams" : teamSelection.type;
      const teamId =
        teamSelection.type === "team" ? teamSelection.teamId : null;

      const { data, error: leaderboardError } = await supabase.rpc(
        "get_sales_leaderboard",
        {
          p_mode: rankingMode,
          p_start_utc: startDateStr,
          p_end_utc: endDateStr,
          p_start_date: formatDateToGermanDateString(localStartDate),
          p_end_date: formatDateToGermanDateString(localEndDate),
          p_selection_type: effectiveSelection,
          p_team_id: teamId,
        }
      );

      if (leaderboardError) throw leaderboardError;

      const transformedData: LeaderboardEntry[] = (
        (data ?? []) as LeaderboardRpcEntry[]
      ).map((entry) => ({
        worker_id: entry.worker_id,
        name: entry.name,
        contract_count: Number(entry.contract_count) || 0,
        revenue:
          entry.revenue === null || typeof entry.revenue === "undefined"
            ? undefined
            : Number(entry.revenue),
      }));

      setLeaderboardData(transformedData);
      setLeaderboardCache((prev) => ({
        ...prev,
        [cacheKey]: transformedData,
      }));
    } catch (error) {
      console.error("Error in fetchLeaderboardData:", error);
      setError(error instanceof Error ? error.message : "An error occurred");
    } finally {
      if (showLoading) hideGlobalLoading();
    }
  };

  const fetchLeaderboardDataRef = useRef(fetchLeaderboardData);

  useEffect(() => {
    fetchLeaderboardDataRef.current = fetchLeaderboardData;
  });

  // Fetch only after identity and role are known. Scope errors fail closed in
  // the RPC instead of falling back to an unfiltered table query.
  useEffect(() => {
    if (accessLoading || roleId === null || currentWorkerId === null) return;
    void fetchLeaderboardData(true);
  }, [
    selectedOption,
    dateRange,
    teamSelection,
    currentWorkerId,
    teamContext,
    hasAccess,
    accessLoading,
    roleId,
  ]);

  // Fetch data when ranking mode changes (without loading)
  useEffect(() => {
    void fetchLeaderboardData(false);
  }, [rankingMode]);

  // Initial setup and subscription
  useEffect(() => {
    const leaderboardSubscription = supabase
      .channel("leaderboard_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "contracts",
        },
        () => {
          void fetchLeaderboardDataRef.current(false, true);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "daily_revenue",
        },
        () => {
          void fetchLeaderboardDataRef.current(false, true);
        }
      )
      .subscribe();

    // Realtime delivery follows table RLS, so roles 4+ will not receive
    // teammate row events. Poll the guarded aggregate endpoint as a fallback.
    const refreshInterval = window.setInterval(() => {
      void fetchLeaderboardDataRef.current(false, true);
    }, 60_000);

    return () => {
      window.clearInterval(refreshInterval);
      void leaderboardSubscription.unsubscribe();
    };
  }, []);

  // Refresh leaderboard when the dashboard refresh button is triggered
  useEffect(() => {
    const unsubscribe = addRefreshListener(() => {
      setLeaderboardCache({});
      void fetchLeaderboardData(true, true);
    });

    return () => {
      unsubscribe();
    };
  }, [
    addRefreshListener,
    rankingMode,
    selectedOption,
    dateRange,
    teamSelection,
    currentWorkerId,
    hasAccess,
    teamContext,
    accessLoading,
    roleId,
  ]);

  if (error) {
    return (
      <div className="h-full rounded-lg bg-white p-4 shadow dark:bg-gray-800 sm:p-6">
        <p className="text-red-500">Error loading leaderboard: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Mobile time filter buttons - only show if admin role */}
      {isAdminRole && (
        <div className="lg:hidden">
          <div className="mb-3 flex items-center justify-start gap-4">
            {/* Toggle for Heute/Gestern */}
            <div className="inline-flex h-8 rounded-md shadow-sm shrink-0">
              <button
                type="button"
                onClick={() => {
                  setSelectedOption("today");
                  setDateRange({
                    startDate: new Date(),
                    endDate: new Date(),
                  });
                }}
                className={`h-8 rounded-l-lg border border-r-0 px-4 text-xs font-medium ${
                  selectedOption === "today"
                    ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                    : "border-gray-200 bg-white text-gray-900 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                }`}
              >
                Heute
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedOption("yesterday");
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  setDateRange({ startDate: yesterday, endDate: yesterday });
                }}
                className={`h-8 rounded-r-lg border px-4 text-xs font-medium ${
                  selectedOption === "yesterday"
                    ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                    : "border-gray-200 bg-white text-gray-900 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                }`}
              >
                Gestern
              </button>
            </div>

            {/* Date range picker with flex grow */}
            <div className="">
              <DateRangePicker
                onChange={(range: DateRange) => {
                  setDateRange(range);
                  setSelectedOption("custom");
                }}
                label=""
                className="date-picker-compact"
              />
            </div>
          </div>
        </div>
      )}

      {/* Main leaderboard card */}
      <div className="h-full rounded-lg bg-white p-4 shadow dark:bg-gray-800 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold leading-none text-gray-900 dark:text-white">
              {getLeaderboardTitle()}
            </h3>
            <p className="text-base font-normal text-gray-600 dark:text-gray-400">
              {getLeaderboardSubtitle()}
            </p>
          </div>
          <div className="flex flex-col items-center">
            <span className="mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">
              {rankingMode === "contracts" ? "Verträge" : "Umsatz"}
            </span>
            {/* Custom toggle switch */}
            {canViewRevenue && (
              <button
                className="relative h-6 w-12 cursor-pointer rounded-full border-0 p-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() =>
                  setRankingMode(
                    rankingMode === "contracts" ? "revenue" : "contracts"
                  )
                }
                style={{
                  backgroundColor:
                    rankingMode === "revenue" ? "#2563eb" : "#d1d5db",
                }}
                aria-label={
                  rankingMode === "contracts"
                    ? "Switch to revenue mode"
                    : "Switch to contracts mode"
                }
                type="button"
              >
                <span className="sr-only">
                  {rankingMode === "contracts"
                    ? "Switch to revenue mode"
                    : "Switch to contracts mode"}
                </span>
                <span
                  className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ease-in-out"
                  style={{
                    transform:
                      rankingMode === "revenue"
                        ? "translateX(1.5rem)"
                        : "translateX(0)",
                  }}
                ></span>
              </button>
            )}
          </div>
        </div>
        <div className="flow-root">
          {leaderboardData.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">
              {!hasAccess
                ? "Heute noch keine Verträge erfasst"
                : selectedOption === "today"
                ? "Heute noch keine Verträge erfasst"
                : selectedOption === "yesterday"
                ? "Gestern keine Verträge erfasst"
                : "Keine Verträge im ausgewählten Zeitraum erfasst"}
            </p>
          ) : (
            <div>
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {leaderboardData.map((rep, index) => (
                  <li key={rep.worker_id} className="py-3">
                    <div className="flex items-center space-x-4">
                      <div className="shrink-0">
                        <span
                          className={`text-lg font-bold ${
                            index === 0
                              ? "text-yellow-500"
                              : index === 1
                              ? "text-gray-400"
                              : index === 2
                              ? "text-orange-700"
                              : "text-gray-600"
                          } dark:text-white`}
                        >
                          #{index + 1}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                          {rep.name}
                        </p>
                      </div>
                      <div className="inline-flex items-center">
                        <span className="px-2.5 py-0.5 text-sm font-medium text-blue-600 dark:text-blue-400">
                          {rankingMode === "contracts"
                            ? `${rep.contract_count} Verträge`
                            : formatCurrency(rep.revenue || 0)}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Divider and total count */}
              {leaderboardData.length > 0 && (
                <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Gesamt
                    </p>
                    <span className="px-2.5 py-0.5 text-sm font-medium text-blue-600 dark:text-blue-400">
                      {rankingMode === "contracts"
                        ? `${leaderboardData.reduce(
                            (sum, rep) => sum + rep.contract_count,
                            0
                          )} Verträge`
                        : formatCurrency(
                            leaderboardData.reduce(
                              (sum, rep) => sum + (rep.revenue || 0),
                              0
                            )
                          )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesLeaderboard;
